import { ParsedUrlQuery } from 'querystring'
import { UrlObject } from 'url'
import { AppBar, Box, Container, Grid, Stack, Toolbar } from '@mui/material'
import Typography from '@mui/material/Typography'
import { C1Button } from 'c1ui'
import { C1LinkButton } from 'components/core/button'
import { APIError } from 'components/core/errors/api'
import GoogleIcon from 'components/core/icons/google'
import JumpCloudIcon from 'components/core/icons/jumpcloud'
import MicrosoftIcon from 'components/core/icons/microsoft'
import OIDCIcon from 'components/core/icons/oidc'
import OktaIcon from 'components/core/icons/okta'
import OneLoginIcon from 'components/core/icons/onelogin'
import PingOneIcon from 'components/core/icons/pingone'
import Image from 'components/core/image'
import { C1Link } from 'components/core/links/C1Link'
import { LoginCard, LoginLayout, LoginTitle } from 'components/core/login-helper'
import { TextField } from 'components/core/textfield'
import { PrivacyAndTerms } from 'components/pages/login/PrivacyAndTerms'
import { listAuthBackends, signup } from 'lib/actions/accounts'
import { AccountsReducer, initialAccountsState } from 'lib/reducers/accounts'
import { routes } from 'lib/routes'
import { GetReturn, Status } from 'lib/rpc'
import { useUnauthedClientRRPC } from 'lib/rpcts'
import { InstallationDomainInputAdornment } from 'lib/tenant'
import { useRouter } from 'next/router'
import {
    AuthBackend,
    AuthBackends,
    Google,
    ListAuthBackendsResponse,
    Microsoft,
} from 'pbts/c1/api/accounts/v1/accounts'
import { AccountsClient } from 'pbts/c1/api/accounts/v1/accounts.client'
import C1TextImage from 'public/static/c1-full.svg'
import { useCallback, useEffect, useReducer, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { SignUpStyles, useStyles } from './styles'
import { AcceptedTerms } from './terms'

export type SignupState = {
    domain?: string
    displayName?: string
    inviteCode?: string
    acceptedTerms: boolean
}

const stringFromQuery = (q: ParsedUrlQuery, key: string): string | undefined => {
    const query = q[key]

    if (!query) {
        return undefined
    }

    if (Array.isArray(query)) {
        if (query.length > 0) {
            return query[0]
        }
        return undefined
    }

    return query
}

const boolFromQuery = (q: ParsedUrlQuery, key: string): boolean => {
    const query = stringFromQuery(q, key)

    if (!query) {
        return false
    }

    return query === 'true'
}

export const SignupStateFromQuery = (q: ParsedUrlQuery): SignupState => {
    return {
        domain: stringFromQuery(q, 'domain'),
        displayName: stringFromQuery(q, 'display_name'),
        inviteCode: stringFromQuery(q, 'invite_code'),
        acceptedTerms: boolFromQuery(q, 'accepted_terms'),
    }
}

export const SignupStateToQuery = (q: ParsedUrlQuery, state: SignupState): ParsedUrlQuery => {
    if (state.domain) {
        q.domain = state.domain
    }
    if (state.displayName) {
        q.display_name = state.displayName
    }
    if (state.inviteCode) {
        q.invite_code = state.inviteCode
    }
    if (state.acceptedTerms) {
        q.accepted_terms = 'true'
    }
    return q
}

export const Header = (): React.ReactElement => {
    const classes = useStyles()

    return (
        <AppBar elevation={0} position="absolute" color="default" className={classes.appBar}>
            <Container className={classes.headerContainer}>
                <Toolbar>
                    <Box flexGrow={1}>
                        <Grid container direction="row" spacing={1}>
                            <Grid item>
                                <Image alt="ConductorOne" src={C1TextImage.src} height={60} width={150} />
                            </Grid>
                        </Grid>
                    </Box>
                    <Box m={1}>
                        <C1LinkButton href={routes.accounts.signup} variant="outlined">
                            <FormattedMessage id="login_header_signup_button" defaultMessage="Sign Up" />
                        </C1LinkButton>
                    </Box>
                    <Box>
                        <C1LinkButton href={routes.accounts.login} variant="contained">
                            <FormattedMessage id="login_header_login_button" defaultMessage="Login" />
                        </C1LinkButton>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    )
}

export const Signup = (): React.ReactElement => {
    const router = useRouter()
    const intl = useIntl()
    const [accountsState, accountsDispatch] = useReducer(AccountsReducer, initialAccountsState)
    const accountsClient = useUnauthedClientRRPC<AccountsClient>(AccountsClient)

    const [domain, setDomain] = useState<string>()
    const [displayName, setDisplayName] = useState<string>()
    const [inviteCode, setInviteCode] = useState<string>()
    const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false)

    const toQuery = (): ParsedUrlQuery => {
        return SignupStateToQuery(router.query, {
            domain,
            displayName,
            inviteCode,
            acceptedTerms,
        })
    }

    useEffect(() => {
        const state = SignupStateFromQuery(router.query)
        if (state.domain) {
            setDomain(state.domain)
        }
        if (state.displayName) {
            setDisplayName(state.displayName)
        }
        if (state.inviteCode) {
            setInviteCode(state.inviteCode)
        }
        if (state.acceptedTerms) {
            setAcceptedTerms(state.acceptedTerms)
        }
    }, [router])

    const signupHandler = useCallback(
        (authBackend: AuthBackend) => {
            if (!domain) {
                return
            }
            if (!displayName) {
                return
            }
            if (!inviteCode) {
                return
            }

            signup(accountsDispatch, accountsClient, domain, displayName, inviteCode, acceptedTerms, authBackend)
        },
        [domain, displayName, inviteCode, acceptedTerms, accountsDispatch, accountsClient],
    )

    const signupGoogle = useCallback(() => {
        signupHandler({ authBackend: { oneofKind: 'google', google: Google.create({}) } })
    }, [signupHandler])

    const signupMicrosoft = useCallback(() => {
        signupHandler({ authBackend: { oneofKind: 'microsoft', microsoft: Microsoft.create({}) } })
    }, [signupHandler])

    const signupOkta = (): void => {
        const url: UrlObject = { pathname: '/accounts/signup/okta', query: toQuery() }
        router.push(url)
    }

    const signupOneLogin = (): void => {
        const url: UrlObject = { pathname: '/accounts/signup/onelogin', query: toQuery() }
        router.push(url)
    }

    const signupJumpCloud = (): void => {
        const url: UrlObject = { pathname: '/accounts/signup/jumpcloud', query: toQuery() }
        router.push(url)
    }

    const signupPingOne = (): void => {
        const url: UrlObject = { pathname: '/accounts/signup/pingone', query: toQuery() }
        router.push(url)
    }

    const signupOIDC = (): void => {
        const url: UrlObject = { pathname: '/accounts/signup/oidc', query: toQuery() }
        router.push(url)
    }

    useEffect(() => {
        if (accountsState.signup.status === Status.OK) {
            // TODO(morgabra) sanity check the url
            const redirectUri = accountsState.signup.item.redirectUri
            router.replace(redirectUri)
        }
    }, [accountsState.signup, router])

    useEffect(() => {
        listAuthBackends(accountsDispatch, accountsClient)
    }, [accountsDispatch, accountsClient])

    const buttons = (authbackends: GetReturn<ListAuthBackendsResponse>): React.ReactElement | null => {
        const buttonElements: React.ReactElement[] = []

        if (authbackends.status === Status.ERROR) {
            return <APIError
                err={authbackends.error}
                title={
                    <FormattedMessage
                        id="component_signup_auth_backends_error"
                        defaultMessage="Error loading auth backends"
                    />
                }
            />
        }

        if (authbackends.status === Status.INIT || authbackends.status === Status.LOADING) {
            return null
        }

        authbackends.item.authBackends.forEach((authbackend) => {
            switch (authbackend) {
                case AuthBackends.AUTH_BACKEND_UNKNOWN:
                    break
                case AuthBackends.AUTH_BACKEND_GOOGLE:
                    buttonElements.push(
                        <C1Button
                            type="submit"
                            fullWidth
                            variant="outlined"
                            startIcon={<GoogleIcon />}
                            onClick={signupGoogle}
                            key="sign-up-google"
                            disabled={!acceptedTerms}
                            sx={SignUpStyles.signUpButton}
                        >
                            <FormattedMessage
                                id="component_signup_button_google"
                                defaultMessage="Sign up with Google"
                            />
                        </C1Button>,
                    )
                    break
                case AuthBackends.AUTH_BACKEND_MICROSOFT:
                    buttonElements.push(
                        <C1Button
                            type="submit"
                            variant="outlined"
                            fullWidth
                            startIcon={<MicrosoftIcon />}
                            onClick={signupMicrosoft}
                            key="sign-up-microsoft"
                            disabled={!acceptedTerms}
                            sx={SignUpStyles.signUpButton}
                        >
                            <FormattedMessage
                                id="component_signup_button_microsoft"
                                defaultMessage="Sign up with Microsoft"
                            />
                        </C1Button>,
                    )
                    break
                case AuthBackends.AUTH_BACKEND_OKTA:
                    buttonElements.push(
                        <C1Button
                            type="submit"
                            fullWidth
                            variant="outlined"
                            startIcon={<OktaIcon />}
                            onClick={signupOkta}
                            key="sign-up-okta"
                            disabled={!acceptedTerms}
                            sx={SignUpStyles.signUpButton}
                        >
                            <FormattedMessage id="component_signup_button_okta" defaultMessage="Sign up with Okta" />
                        </C1Button>,
                    )
                    break
                case AuthBackends.AUTH_BACKEND_ONELOGIN:
                    buttonElements.push(
                        <C1Button
                            type="submit"
                            fullWidth
                            variant="outlined"
                            startIcon={<OneLoginIcon />}
                            onClick={signupOneLogin}
                            key="sign-up-onelogin"
                            disabled={!acceptedTerms}
                            sx={SignUpStyles.signUpButton}
                        >
                            <FormattedMessage
                                id="component_signup_button_one_login"
                                defaultMessage="Sign up with OneLogin"
                            />
                        </C1Button>,
                    )
                    break
                case AuthBackends.AUTH_BACKEND_JUMPCLOUD:
                    buttonElements.push(
                        <C1Button
                            type="submit"
                            fullWidth
                            variant="outlined"
                            startIcon={<JumpCloudIcon />}
                            onClick={signupJumpCloud}
                            key="sign-up-jumpcloud"
                            disabled={!acceptedTerms}
                            sx={SignUpStyles.signUpButton}
                        >
                            <FormattedMessage
                                id="component_signup_button_jump_cloud"
                                defaultMessage="Sign up with JumpCloud"
                            />
                        </C1Button>,
                    )
                    break
                case AuthBackends.AUTH_BACKEND_PINGONE:
                    buttonElements.push(
                        <C1Button
                            type="submit"
                            fullWidth
                            variant="outlined"
                            startIcon={<PingOneIcon />}
                            onClick={signupPingOne}
                            key="sign-up-pingone"
                            disabled={!acceptedTerms}
                            sx={SignUpStyles.signUpButton}
                        >
                            <FormattedMessage
                                id="component_signup_button_ping_one"
                                defaultMessage="Sign up with PingOne"
                            />
                        </C1Button>,
                    )
                    break
                case AuthBackends.AUTH_BACKEND_OIDC:
                    buttonElements.push(
                        <C1Button
                            type="submit"
                            fullWidth
                            variant="outlined"
                            startIcon={<OIDCIcon />}
                            onClick={signupOIDC}
                            key="sign-up-oidc"
                            disabled={!acceptedTerms}
                            sx={SignUpStyles.signUpButton}
                        >
                            <FormattedMessage
                                id="component_signup_button_oidc"
                                defaultMessage="Sign up with OIDC"
                            />
                        </C1Button>,
                    )
                    break
                // no default
            }
        })

        return <>{buttonElements}</>
    }

    return (
        <LoginLayout>
            <LoginCard>
                <LoginTitle>
                    <FormattedMessage id="component_signup_title" defaultMessage="Create account" />
                </LoginTitle>

                {accountsState.signup.status === Status.ERROR && (
                    <APIError
                        err={accountsState.signup.error}
                        title={
                            <FormattedMessage
                                id="component_signup_lookup_error"
                                defaultMessage="Error"
                            />
                        }
                    />
                )}

                <Stack spacing={2}>
                    <TextField
                        value={domain || ''}
                        onChange={(event): void => {
                            setDomain(event.target.value)
                        }}
                        required
                        id="domain"
                        name="domain"
                        label={intl.formatMessage({
                            id: 'login_domain_title',
                            defaultMessage: 'Domain',
                        })}
                        InputProps={{
                            endAdornment: <InstallationDomainInputAdornment />,
                        }}
                    />
                    <TextField
                        value={displayName || ''}
                        onChange={(event): void => {
                            setDisplayName(event.target.value)
                        }}
                        required
                        id="display-name"
                        name="display-name"
                        label={intl.formatMessage({
                            id: 'signup_display_name',
                            defaultMessage: 'Display name',
                        })}
                    />
                    <TextField
                        value={inviteCode || ''}
                        onChange={(event): void => {
                            setInviteCode(event.target.value)
                        }}
                        required
                        id="invite-code"
                        name="invite-code"
                        label={intl.formatMessage({
                            id: 'signup_invite-code',
                            defaultMessage: 'Invite code',
                        })}
                    />
                </Stack>

                <AcceptedTerms acceptedTerms={acceptedTerms} setAcceptedTerms={setAcceptedTerms} />

                <Stack spacing={2}>
                    {buttons(accountsState.authbackends)}
                </Stack>

                <Stack spacing={3} component="footer" sx={{ textAlign: 'center' }}>
                    <Typography sx={{ color: 'text.secondary' }}>
                        <FormattedMessage
                            id="component_signup_login_prompt"
                            defaultMessage="Need to log in instead? <login>Log in</login>"
                            values={{
                                login: (chunks) => <C1Link href={routes.accounts.login}>{chunks}</C1Link>,
                            }}
                        />
                    </Typography>

                    <PrivacyAndTerms />
                </Stack>
            </LoginCard>
        </LoginLayout>
    )
}

export default Signup
